import { gsap } from 'gsap'

import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// One gsap context per root, so re-initialising a root reverts its previous run
// instead of stacking a second set of ScrollTriggers on the same elements.
const revealContexts = new WeakMap()

/**
 * @param {Document|Element|string} [root] Limits the reveal to the groups inside
 * this element (or selector). Defaults to the whole document.
 * @returns {() => void} Reverts everything this call set up.
 */
function initContentRevealScroll(root = document) {
    const rootEl =
        typeof root === 'string' ? document.querySelector(root) : root

    if (!rootEl || (rootEl !== document && rootEl.nodeType !== 1)) {
        return () => {}
    }

    // Revert a previous run on this same root (htmx swapping a component's
    // content is the usual reason) and drop triggers left on removed elements.
    revealContexts.get(rootEl)?.()

    /**
     * Reverting a finished tween hands its target back the values it started
     * from, and for a completed reveal that means hidden again. When content
     * is appended to a group instead of replacing it — a Sprig load more —
     * those targets are still on screen, so the revert would blank out items
     * the visitor is looking at.
     *
     * Anything already revealed is therefore put back on screen explicitly.
     * Clearing the inline styles instead would not do: critical-css.twig
     * pre-hides every child of a reveal group so nothing flashes before this
     * runs, so an element without inline styles falls back to hidden. This
     * sits outside the new context, so it cannot be reverted in turn.
     */
    const revealed =
        rootEl !== document && rootEl.matches('[data-reveal-done]')
            ? [rootEl, ...rootEl.querySelectorAll('[data-reveal-done]')]
            : [...rootEl.querySelectorAll('[data-reveal-done]')]

    if (revealed.length) {
        gsap.set(revealed, { y: 0, autoAlpha: 1 })
    }

    ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.trigger && !trigger.trigger.isConnected) {
            trigger.kill()
        }
    })

    const prefersReduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
    ).matches

    const ctx = gsap.context(() => {
        const groups = Array.from(
            rootEl.querySelectorAll('[data-reveal-group]')
        )

        if (rootEl !== document && rootEl.matches('[data-reveal-group]')) {
            groups.unshift(rootEl)
        }

        groups.forEach((groupEl) => {
            // Config from attributes or defaults (group-level)
            const groupStaggerSec =
                (parseFloat(groupEl.getAttribute('data-stagger')) || 100) / 1000 // ms → sec
            const groupDistance = groupEl.getAttribute('data-distance') || '2em'
            const triggerStart = groupEl.getAttribute('data-start') || 'top 90%'

            const animDuration = 0.8
            const animEase = 'power4.inOut'
            const showMarkers = false

            // Reduced motion: show immediately
            if (prefersReduced) {
                gsap.set(groupEl, { clearProps: 'all', y: 0, autoAlpha: 1 })
                return
            }

            // If no direct children, animate the group element itself
            const directChildren = Array.from(groupEl.children).filter(
                (el) => el.nodeType === 1
            )
            if (!directChildren.length) {
                gsap.set(groupEl, { y: groupDistance, autoAlpha: 0 })
                ScrollTrigger.create({
                    trigger: groupEl,
                    start: triggerStart,
                    markers: showMarkers,
                    once: true,
                    onEnter: () =>
                        gsap.to(groupEl, {
                            y: 0,
                            autoAlpha: 1,
                            duration: animDuration,
                            ease: animEase
                            // onComplete: () =>
                            //     gsap.set(groupEl, { clearProps: 'all' })
                        })
                })
                return
            }

            // Build animation slots: item or nested (deep layers allowed)
            const slots = []
            directChildren.forEach((child) => {
                /**
                 * Children that have already been revealed keep what they
                 * have: no hidden start state, no second tween. Appending to
                 * a group that is already on screen (a Sprig load more, say)
                 * re-runs this on the whole group, which would otherwise make
                 * the items the visitor is looking at fade in all over again.
                 */
                if (child.matches('[data-reveal-done]')) {
                    return
                }

                const nestedGroup = child.matches('[data-reveal-group-nested]')
                    ? child
                    : child.querySelector(':scope [data-reveal-group-nested]')

                if (nestedGroup) {
                    const includeParent =
                        child.getAttribute('data-ignore') === 'false' ||
                        nestedGroup.getAttribute('data-ignore') === 'false'
                    slots.push({
                        type: 'nested',
                        parentEl: child,
                        nestedEl: nestedGroup,
                        includeParent
                    })
                } else if (!child.matches('[data-top-title]')) {
                    slots.push({ type: 'item', el: child })
                }
            })

            // Nothing left to reveal, so no trigger to leave behind either.
            if (!slots.length) {
                return
            }

            // Initial hidden state
            slots.forEach((slot) => {
                if (slot.type === 'item') {
                    // If the element itself is a nested group, force group distance (prevents it from using its own data-distance)
                    const isNestedSelf = slot.el.matches(
                        '[data-reveal-group-nested]'
                    )
                    const d = isNestedSelf
                        ? groupDistance
                        : slot.el.getAttribute('data-distance') || groupDistance
                    gsap.set(slot.el, { y: d, autoAlpha: 0 })
                } else {
                    // Parent follows the group's distance when included, regardless of nested's data-distance
                    if (slot.includeParent)
                        gsap.set(slot.parentEl, {
                            y: groupDistance,
                            autoAlpha: 0
                        })
                    // Children use nested group's own distance (fallback to group distance)
                    const nestedD =
                        slot.nestedEl.getAttribute('data-distance') ||
                        groupDistance
                    Array.from(slot.nestedEl.children).forEach((target) =>
                        gsap.set(target, { y: nestedD, autoAlpha: 0 })
                    )
                }
            })

            // Extra safety: if a nested parent is included, re-assert its distance to the group's value
            slots.forEach((slot) => {
                if (slot.type === 'nested' && slot.includeParent) {
                    gsap.set(slot.parentEl, { y: groupDistance })
                }
            })

            // Reveal sequence
            ScrollTrigger.create({
                trigger: groupEl,
                start: triggerStart,
                markers: showMarkers,
                once: true,
                onEnter: () => {
                    const tl = gsap.timeline()

                    /**
                     * Marked here rather than when the slots are built: an
                     * item that is still waiting below the fold has to stay
                     * eligible, otherwise a re-run would skip it and leave it
                     * sitting at opacity 0 forever.
                     */
                    slots.forEach((slot) => {
                        const el =
                            slot.type === 'item' ? slot.el : slot.parentEl
                        el.setAttribute('data-reveal-done', '')
                    })

                    slots.forEach((slot, slotIndex) => {
                        const slotTime = slotIndex * groupStaggerSec

                        if (slot.type === 'item') {
                            tl.to(
                                slot.el,
                                {
                                    y: 0,
                                    autoAlpha: 1,
                                    duration: animDuration,
                                    ease: animEase
                                    // onComplete: () =>
                                    //     gsap.set(slot.el, { clearProps: 'all' })
                                },
                                slotTime
                            )
                        } else {
                            // Optionally include the parent at the same slot time (parent uses group distance)
                            if (slot.includeParent) {
                                tl.to(
                                    slot.parentEl,
                                    {
                                        y: 0,
                                        autoAlpha: 1,
                                        duration: animDuration,
                                        ease: animEase
                                        // onComplete: () =>
                                        //     gsap.set(slot.parentEl, {
                                        //         clearProps: 'all'
                                        //     })
                                    },
                                    slotTime
                                )
                            }
                            // Nested children use nested stagger (ms → sec); fallback to group stagger
                            const nestedMs = parseFloat(
                                slot.nestedEl.getAttribute('data-stagger')
                            )
                            const nestedStaggerSec = isNaN(nestedMs)
                                ? groupStaggerSec
                                : nestedMs / 1000
                            Array.from(slot.nestedEl.children).forEach(
                                (nestedChild, nestedIndex) => {
                                    tl.to(
                                        nestedChild,
                                        {
                                            y: 0,
                                            autoAlpha: 1,
                                            duration: animDuration,
                                            ease: animEase
                                            // onComplete: () =>
                                            //     gsap.set(nestedChild, {
                                            //         clearProps: 'all'
                                            //     })
                                        },
                                        slotTime +
                                            nestedIndex * nestedStaggerSec
                                    )
                                }
                            )
                        }
                    })
                }
            })
        })
    })

    const revert = () => {
        ctx.revert()
        revealContexts.delete(rootEl)
    }

    revealContexts.set(rootEl, revert)

    return revert
}

document.addEventListener('DOMContentLoaded', () => {
    initContentRevealScroll()

    if (window.htmx !== undefined) {
        /**
         * Only reveal what was actually swapped in. `htmx:afterSwap` fires on
         * the swapped element itself (the sprig component, since sprig targets
         * `this`), so content outside of it keeps the state it already had.
         */
        htmx.on('htmx:afterSwap', function (event) {
            const swapped =
                event.target instanceof Element
                    ? event.target
                    : event.detail?.target

            if (swapped instanceof Element) {
                initContentRevealScroll(swapped)
            }
        })
    }
})
